/**
 * The plan-limit channel: one place a 402 becomes an upgrade dialog.
 *
 * Every request in the app goes through `authenticatedFetch`, and every caller of it does its
 * own `if (!response.ok) throw new Error(...)`. That is fine for errors a page should explain,
 * but a plan limit is not one of those, it is the same message every time, it needs a call to
 * action rather than a sentence, and it can arrive from publish, from connecting a number, and
 * from every Pro feature workstreams A, B and C are still building. Wiring it per page would
 * mean the pages built after this one quietly do not have it.
 *
 * So the detection lives in the central client and the result is published here. A tiny event
 * bus rather than a context because the publisher (AuthContext) sits ABOVE the consumer in the
 * provider tree, and a context cannot be read by its own ancestor.
 */

import { track } from './analytics';

const listeners = new Set();

/**
 * Subscribe to plan limits. Returns an unsubscribe function.
 *
 * @param {(detail: {code: string, limit: string, plan: string, message?: string}) => void} listener
 */
export const onPlanLimit = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Publish a plan limit to every subscriber. */
export const emitPlanLimit = (detail) => {
  listeners.forEach((listener) => {
    try {
      listener(detail);
    } catch (error) {
      // A broken listener must never break the request that triggered it.
      console.error('Plan-limit listener failed:', error);
    }
  });
};

/**
 * Recognise a plan limit in a response and announce it.
 *
 * The response is cloned before reading, so the caller still gets an unread body and behaves
 * exactly as it did before. Anything that is not a `PLAN_LIMIT` 402, including a 402 with an
 * unreadable body, passes through untouched.
 *
 * @returns {Promise<boolean>} whether a plan limit was announced
 */
export const reportPlanLimit = async (response) => {
  if (!response || response.status !== 402) return false;
  try {
    const body = await response.clone().json();
    if (body?.code !== 'PLAN_LIMIT') return false;
    // Launch funnel event (docs/launch/ws-I.md), fired from the one choke point every 402
    // PLAN_LIMIT passes through, so a limit added next week gets tracked with no UI work.
    track('plan_limit_hit', { limit: body.limit });
    emitPlanLimit(body);
    return true;
  } catch (error) {
    return false;
  }
};
